import React, { useState, useEffect } from 'react';
import { X, Save, Calendar, User, Trash2, CheckCircle, AlertTriangle } from 'lucide-react';
import { vacancesService } from '../services/api';
import { format, addWeeks, startOfWeek, endOfWeek, getISOWeek } from 'date-fns';
import { fr } from 'date-fns/locale';

const VacancesModal = ({ 
  isOpen, 
  onClose, 
  personne, 
  onVacancesUpdated,
  initialWeek = null 
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [errors, setErrors] = useState({});
  const [existingVacances, setExistingVacances] = useState([]);
  
  // Form state
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  const [joursVacances, setJoursVacances] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [weekInputType, setWeekInputType] = useState('single'); // 'single' ou 'range'
  const [singleWeek, setSingleWeek] = useState('');
  const [startWeek, setStartWeek] = useState('');
  const [endWeek, setEndWeek] = useState('');

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen && personne) {
      resetForm();
      loadExistingVacances();
      
      // Si une semaine initiale est fournie, la pré-sélectionner
      if (initialWeek) {
        const weekString = typeof initialWeek === 'string' ? initialWeek : formatWeekInput(initialWeek);
        setSingleWeek(weekString);
        setSelectedWeeks([weekString]);
        setWeekInputType('single');
      }
    }
  }, [isOpen, personne, initialWeek]);

  const resetForm = () => {
    setSelectedWeeks([]);
    setJoursVacances('');
    setCommentaire('');
    setSingleWeek('');
    setStartWeek('');
    setEndWeek('');
    setWeekInputType('single');
    setErrors({});
    setSaved(false);
    setExistingVacances([]);
  };

  const formatWeekInput = (date) => {
    const weekStart = startOfWeek(date, { weekStartsOn: 1 });
    const year = weekStart.getFullYear();
    const weekNumber = getISOWeek(weekStart);
    return `${year}-W${weekNumber.toString().padStart(2, '0')}`;
  };

  const weekInputToDate = (weekString) => {
    const [year, week] = weekString.split('-W').map(Number);
    const jan4 = new Date(year, 0, 4);
    const jan4WeekStart = startOfWeek(jan4, { weekStartsOn: 1 });
    return addWeeks(jan4WeekStart, week - 1);
  };

  const formatDateForAPI = (date) => {
    const weekStart = startOfWeek(date, { weekStartsOn: 1 });
    return format(weekStart, 'yyyy-MM-dd');
  };

  const loadExistingVacances = async () => {
    try {
      setLoading(true);
      const response = await vacancesService.getByPersonne(personne.id);
      setExistingVacances(response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des vacances:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update selected weeks based on input type
  useEffect(() => {
    if (weekInputType === 'single' && singleWeek) {
      setSelectedWeeks([singleWeek]);
    } else if (weekInputType === 'range' && startWeek && endWeek) {
      const weeks = [];
      let currentWeek = weekInputToDate(startWeek);
      const endWeekDate = weekInputToDate(endWeek);
      
      while (currentWeek <= endWeekDate) {
        weeks.push(formatWeekInput(currentWeek));
        currentWeek = addWeeks(currentWeek, 1);
      }
      setSelectedWeeks(weeks);
    } else {
      setSelectedWeeks([]);
    }
  }, [weekInputType, singleWeek, startWeek, endWeek]);

  const validateForm = () => {
    const newErrors = {};
    
    if (selectedWeeks.length === 0) {
      newErrors.weeks = 'Sélectionnez au moins une semaine';
    }
    
    if (!joursVacances || parseFloat(joursVacances) < 0 || parseFloat(joursVacances) > 5) {
      newErrors.joursVacances = 'Le nombre de jours doit être entre 0 et 5';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      
      // Convertir les semaines au format API (dates des lundis)
      const semaines = selectedWeeks.map(week => formatDateForAPI(weekInputToDate(week)));
      
      const vacancesData = {
        id_personne: personne.id,
        semaines,
        jours_vacances: parseFloat(joursVacances),
        commentaire: commentaire.trim() || null
      };

      await vacancesService.createOrUpdate(vacancesData);
      
      setSaved(true);
      await loadExistingVacances(); // Recharger les vacances
      
      if (onVacancesUpdated) {
        onVacancesUpdated();
      }
      
      setTimeout(() => {
        setSaved(false);
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des vacances:', error);
      setErrors({ save: 'Erreur lors de la sauvegarde' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVacances = async (vacancesId) => {
    try {
      await vacancesService.delete(vacancesId);
      await loadExistingVacances(); // Recharger les vacances
      
      if (onVacancesUpdated) {
        onVacancesUpdated();
      }
    } catch (error) {
      console.error('Erreur lors de la suppression des vacances:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <Calendar className="h-5 w-5 mr-2 text-primary-600" />
                Gestion des vacances
              </h2>
              {personne && (
                <p className="text-sm text-gray-600 mt-1 flex items-center">
                  <User className="h-4 w-4 mr-1" />
                  {personne.prenom} {personne.nom}
                </p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {saved && (
                <div className="flex items-center text-green-600">
                  <CheckCircle className="h-5 w-5 mr-1" />
                  <span className="text-sm font-medium">Sauvegardé</span>
                </div>
              )}
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Vacances existantes */}
          {existingVacances.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Vacances existantes</h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {existingVacances.map((vacances) => (
                  <div key={vacances.id} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Calendar className="h-4 w-4 text-blue-600" />
                      <div>
                        <span className="text-sm font-medium text-blue-900">
                          Semaine du {format(new Date(vacances.semaine), 'dd MMMM yyyy', { locale: fr })}
                        </span>
                        <span className="mx-2 text-blue-700">•</span>
                        <span className="text-sm text-blue-700">
                          {vacances.jours_vacances} jour{vacances.jours_vacances > 1 ? 's' : ''}
                        </span>
                        {vacances.commentaire && (
                          <div className="text-xs text-blue-600 mt-1">{vacances.commentaire}</div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteVacances(vacances.id)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Supprimer ces vacances"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Formulaire */}
          <div className="space-y-6">
            {/* Type de sélection */}
            <div>
              <label className="form-label">Type de sélection</label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="single"
                    checked={weekInputType === 'single'}
                    onChange={(e) => setWeekInputType(e.target.value)}
                    className="mr-2"
                  />
                  Semaine unique
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="range"
                    checked={weekInputType === 'range'}
                    onChange={(e) => setWeekInputType(e.target.value)}
                    className="mr-2"
                  />
                  Plage de semaines
                </label>
              </div>
            </div>

            {/* Sélection des semaines */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {weekInputType === 'single' ? (
                <div>
                  <label className="form-label">Semaine *</label>
                  <input
                    type="week"
                    value={singleWeek}
                    onChange={(e) => setSingleWeek(e.target.value)}
                    className={`form-input ${errors.weeks ? 'border-red-500' : ''}`}
                  />
                </div>
              ) : (
                <>
                  <div>
                    <label className="form-label">Semaine de début *</label>
                    <input
                      type="week"
                      value={startWeek}
                      onChange={(e) => setStartWeek(e.target.value)}
                      className={`form-input ${errors.weeks ? 'border-red-500' : ''}`}
                    />
                  </div>
                  <div>
                    <label className="form-label">Semaine de fin *</label>
                    <input
                      type="week"
                      value={endWeek}
                      onChange={(e) => setEndWeek(e.target.value)}
                      className={`form-input ${errors.weeks ? 'border-red-500' : ''}`}
                    />
                  </div>
                </>
              )}

              <div>
                <label className="form-label">Jours de vacances *</label>
                <input
                  type="number"
                  value={joursVacances}
                  onChange={(e) => setJoursVacances(e.target.value)}
                  step="0.5"
                  min="0"
                  max="5"
                  className={`form-input ${errors.joursVacances ? 'border-red-500' : ''}`}
                  placeholder="2.5"
                />
                {errors.joursVacances && (
                  <p className="mt-1 text-sm text-red-600">{errors.joursVacances}</p>
                )}
              </div>
            </div>

            {errors.weeks && (
              <p className="mt-1 text-sm text-red-600">{errors.weeks}</p>
            )}

            {/* Semaines sélectionnées */}
            {selectedWeeks.length > 0 && (
              <div>
                <label className="form-label">
                  Semaines sélectionnées ({selectedWeeks.length})
                </label>
                <div className="max-h-32 overflow-y-auto p-3 bg-gray-50 rounded-lg">
                  <div className="flex flex-wrap gap-2">
                    {selectedWeeks.map((week, index) => {
                      const weekDate = weekInputToDate(week);
                      const weekStart = startOfWeek(weekDate, { weekStartsOn: 1 });
                      const weekEnd = endOfWeek(weekDate, { weekStartsOn: 1 });
                      return (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                        >
                          {format(weekStart, 'dd MMM', { locale: fr })} - {format(weekEnd, 'dd MMM', { locale: fr })}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Commentaire */}
            <div>
              <label className="form-label">Commentaire (optionnel)</label>
              <textarea
                value={commentaire}
                onChange={(e) => setCommentaire(e.target.value)}
                className="form-input"
                rows="3"
                placeholder="Type de vacances, raison, etc..."
                maxLength="1000"
              />
              <p className="mt-1 text-sm text-gray-500">
                {commentaire.length}/1000 caractères
              </p>
            </div>

            {/* Erreur de sauvegarde */}
            {errors.save && (
              <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
                <span className="text-red-700">{errors.save}</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Annuler
            </button>
            <button 
              onClick={handleSave}
              disabled={saving || selectedWeeks.length === 0 || !joursVacances}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VacancesModal;
